import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import {
  LabeledBar, Pill, SeverityBadge, fmtDate, label,
} from '@/features/dossier/components/primitives'
import { listSurfaces, listTargets, type Surface, type Target } from './api'

/**
 * Every documented exposure, across every target.
 *
 * ## Evidence strength has no control, anywhere
 *
 * It is derived from the linked items — count, source independence, Admiralty grade — and
 * there is no input for it on the server either. An analyst who could type it would type 90.
 * It is rendered in a different tone from the three human sliders beside it so the difference
 * is visible rather than a convention somebody has to know.
 *
 * ## STALE is shown loudly
 *
 * A surface goes STALE automatically when a linked item is disputed or debunked. Continuing
 * to push a claim whose evidence has fallen over is how an operation becomes the story, so
 * this screen does not let that state be quiet.
 */
export function SurfacesPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<{ target: Target; surfaces: Surface[] }[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const targets = await listTargets()
      const withSurfaces = await Promise.all(targets.map(async (target) => ({
        target, surfaces: await listSurfaces(target.id),
      })))
      setRows(withSurfaces.filter((r) => r.surfaces.length > 0))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading surfaces" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Vulnerability Surfaces"
        description="What is documented, how well it is evidenced, and how easily they could
                     answer it."
      />

      {rows.length === 0 && (
        <Panel>
          <p className="px-3 py-6 text-center text-[12px] text-muted-foreground">
            No exposures recorded yet. A surface needs a rationale of at least 40 characters —
            an unexplained severity ends up in a press briefing.
          </p>
        </Panel>
      )}

      {rows.map(({ target, surfaces }) => (
        <Panel key={target.id}>
          <div className="space-y-2 p-2">
            <button type="button" onClick={() => navigate(`/opposition/targets/${target.id}`)}
                    className="text-[12px] font-semibold hover:underline">
              {target.displayName}
              <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                {surfaces.length} surface{surfaces.length === 1 ? '' : 's'}
              </span>
            </button>
            {surfaces.map((s) => (
              <div key={s.id} className="rounded border border-border p-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-[12px] font-medium">{s.surface}</span>
                  <div className="flex items-center gap-1.5">
                    <Pill className="border-border">{label(s.categoryCode)}</Pill>
                    {s.statusCode === 'STALE'
                      ? <SeverityBadge severity="HIGH" />
                      : <Pill className="border-border">{label(s.statusCode)}</Pill>}
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{s.rationale}</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-4">
                  <LabeledBar label="Severity" value={s.severity} />
                  <LabeledBar label="Public exposure" value={s.publicExposure} />
                  <LabeledBar label="Defensibility" value={s.defensibility} />
                  {/* Different tone: this one is computed, not judged. */}
                  <LabeledBar label="Evidence (derived)" value={s.evidenceStrength}
                              tone="bg-emerald-500" />
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                  <span>{s.evidence.length} linked item{s.evidence.length === 1 ? '' : 's'}</span>
                  <span>first seen {fmtDate(s.firstSeenAt)}</span>
                  {s.lastReviewedAt && <span>reviewed {fmtDate(s.lastReviewedAt)}</span>}
                </div>
                {s.statusCode === 'STALE' && (
                  <p className="mt-1 text-[11px] text-rose-600">
                    Evidence behind this was disputed or debunked. It will also block a SUSTAIN
                    move until somebody reviews it.
                  </p>
                )}
                {s.recommendation && (
                  <p className="mt-1 text-[11px]">
                    <span className="text-muted-foreground">Recommendation: </span>
                    {s.recommendation}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  )
}
